import { Fragment } from "react";

import { renderMathToHtml } from "@/lib/math/render-utils";

type MathSegment =
  | { type: "text"; value: string }
  | { type: "inline"; value: string }
  | { type: "block"; value: string };

const MATH_PATTERN =
  /\\\[([\s\S]+?)\\\]|\\\(([\s\S]+?)\\\)|\$\$([\s\S]+?)\$\$|\$([^$]+?)\$/g;

function splitMathSegments(text: string): MathSegment[] {
  const segments: MathSegment[] = [];
  let lastIndex = 0;

  text.replace(
    MATH_PATTERN,
    (
      match,
      blockMatch1: string | undefined,
      inlineMatch1: string | undefined,
      blockMatch2: string | undefined,
      inlineMatch2: string | undefined,
      offset: number
    ) => {
      if (offset > lastIndex) {
        segments.push({
          type: "text",
          value: text.slice(lastIndex, offset),
        });
      }

      if (blockMatch1 !== undefined || blockMatch2 !== undefined) {
        segments.push({
          type: "block",
          value: (blockMatch1 ?? blockMatch2 ?? "").trim(),
        });
      } else {
        segments.push({
          type: "inline",
          value: (inlineMatch1 ?? inlineMatch2 ?? "").trim(),
        });
      }

      lastIndex = offset + match.length;
      return match;
    }
  );

  if (lastIndex < text.length) {
    segments.push({
      type: "text",
      value: text.slice(lastIndex),
    });
  }

  return segments;
}

export function renderInlineMath(
  text: string,
  keyPrefix: string,
  scrollLabel?: string
) {
  return splitMathSegments(text).map((segment, index) => {
    if (segment.type === "text") {
      return (
        <Fragment key={`${keyPrefix}-text-${index}`}>{segment.value}</Fragment>
      );
    }

    if (segment.type === "block") {
      return (
        <div
          key={`${keyPrefix}-math-${index}`}
          className="math-render math-render--display"
          data-testid="react-katex"
          dangerouslySetInnerHTML={{
            __html: renderMathToHtml(segment.value, true),
          }}
        />
      );
    }

    return (
      <span
        key={`${keyPrefix}-math-${index}`}
        className="math-render math-render--inline"
        role={scrollLabel ? "region" : undefined}
        aria-label={scrollLabel}
        // Keyboard focus lets users scroll a bounded equation with arrow keys.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={scrollLabel ? 0 : undefined}
        style={
          scrollLabel
            ? {
                display: "inline-block",
                maxWidth: "100%",
                overflowX: "auto",
                whiteSpace: "nowrap",
                verticalAlign: "middle",
              }
            : undefined
        }
        data-testid="react-katex"
        dangerouslySetInnerHTML={{
          __html: renderMathToHtml(segment.value, false),
        }}
      />
    );
  });
}
